import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import styles from './InDepthMember.module.scss';
import {
  Member,
  SessionStatistic,
  ExerciseSetStatistic,
  Exercise,
} from '../../types';
import ListContent from '../composites/ListContent';
import EmptyState from '../composites/EmptyState';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';
import { getFormalDateString } from '../../utils/dashboardUtils';
import WorkoutDetailsDialog from './WorkoutDetailsDialog';
import {
  useSessionAction,
  useSessionData,
  useUserAction,
} from '../../providers';
import { cloneDeep } from 'lodash';
import Edit1RMDialog from './Edit1RMDialog';
import ExerciseSetHistoryDialog from './ExerciseSetHistoryDialog';
import TabSlider, { TabSliderTab } from '../composites/TabSlider';
import { fetchExercisesByIds } from '../../api';

const formatDate = (dateString: string): string => {
  if (!dateString) {
    return '--';
  }
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

enum InDepthMemberContentTabs {
  OneRMs = '1RMs',
  Workouts = 'Workouts',
}

interface InDepthMemberProps {
  member: Member;
}

interface OneRMRow {
  id: string;
  exerciseId: string;
  exerciseName: string;
  max1RM: number | string;
  lastUpdateDate: string;
  initial1RM: number | string;
  firstUpdateDate: string;
  growthPercentage: string;
}

const normalizeExerciseName = (name: string): string =>
  name.trim().toLowerCase();

const collectExerciseIdentityKeys = (
  exerciseId: string,
  exercise?: Exercise,
): string[] => {
  return [exerciseId, exercise?.originalExerciseId].filter(
    (value): value is string => Boolean(value),
  );
};

const buildOneRMRows = (
  member: Member,
  globalExercises: Record<string, Exercise>,
  nameOverrides: Record<string, string> = {},
): OneRMRow[] => {
  const memberExerciseRecords = member.records?.exercises || {};
  const exercisesWithRecords: OneRMRow[] = [];
  const exercisesWithoutRecords: OneRMRow[] = [];

  const exerciseIds = Array.from(
    new Set([
      ...Object.keys(globalExercises || {}),
      ...Object.keys(memberExerciseRecords || {}),
    ]),
  );

  const seenExerciseNames = new Set<string>();
  const seenExerciseIds = new Set<string>();

  const resolveExerciseName = (exerciseId: string, exercise?: Exercise) =>
    exercise?.name || nameOverrides[exerciseId] || exerciseId;

  const markExerciseSeen = (exerciseId: string, exerciseName: string) => {
    const exercise = globalExercises[exerciseId];
    const nameKey = normalizeExerciseName(exerciseName);
    if (nameKey) {
      seenExerciseNames.add(nameKey);
    }
    collectExerciseIdentityKeys(exerciseId, exercise).forEach((id) => {
      seenExerciseIds.add(id);
    });
  };

  const hasSeenExercise = (
    exerciseId: string,
    exerciseName: string,
  ): boolean => {
    const exercise = globalExercises[exerciseId];
    const nameKey = normalizeExerciseName(exerciseName);
    return (
      Boolean(nameKey && seenExerciseNames.has(nameKey)) ||
      collectExerciseIdentityKeys(exerciseId, exercise).some((id) =>
        seenExerciseIds.has(id),
      )
    );
  };

  exerciseIds.forEach((exerciseId) => {
    const exercise = globalExercises[exerciseId];
    const record = memberExerciseRecords[exerciseId];
    const exerciseName = resolveExerciseName(exerciseId, exercise);

    if (!record || hasSeenExercise(exerciseId, exerciseName)) {
      return;
    }

    let growthPercentage = '0.0';
    if (record.max1RM?.oneRM != null) {
      const max = record.max1RM.oneRM;
      const initial = record.initial1RM?.oneRM ?? record.max1RM.oneRM;
      if (initial > 0) {
        const growth = (max / initial - 1) * 100;
        growthPercentage = (growth > 0 ? '+' : '') + growth.toFixed(1);
      } else if (max > 0) {
        growthPercentage = 'N/A';
      }
    } else {
      growthPercentage = '--';
    }

    exercisesWithRecords.push({
      id: exerciseId,
      exerciseId,
      exerciseName,
      max1RM: record.max1RM?.oneRM ?? '--',
      lastUpdateDate: record.max1RM?.updatedDate
        ? record.max1RM.updatedDate
        : '',
      initial1RM: record.initial1RM?.oneRM ?? record.max1RM?.oneRM ?? '--',
      firstUpdateDate:
        record.initial1RM?.updatedDate ?? record.max1RM?.updatedDate ?? '',
      growthPercentage,
    });
    markExerciseSeen(exerciseId, exerciseName);
  });

  exerciseIds.forEach((exerciseId) => {
    const exercise = globalExercises[exerciseId];
    const record = memberExerciseRecords[exerciseId];
    const exerciseName = resolveExerciseName(exerciseId, exercise);

    if (record || hasSeenExercise(exerciseId, exerciseName)) {
      return;
    }

    exercisesWithoutRecords.push({
      id: exerciseId,
      exerciseId,
      exerciseName,
      max1RM: '--',
      lastUpdateDate: '',
      initial1RM: '--',
      firstUpdateDate: '',
      growthPercentage: '--',
    });
    markExerciseSeen(exerciseId, exerciseName);
  });

  exercisesWithRecords.sort((a, b) =>
    a.exerciseName.localeCompare(b.exerciseName),
  );
  exercisesWithoutRecords.sort((a, b) =>
    a.exerciseName.localeCompare(b.exerciseName),
  );

  return [...exercisesWithRecords, ...exercisesWithoutRecords];
};

const InDepthMember: React.FC<InDepthMemberProps> = ({ member }) => {
  const { updateMember, getSessionStatistics } = useUserAction();
  const { ensureExerciseLibraryLoaded } = useSessionAction();
  const { mergedExercisesMap, members } = useSessionData();
  const getSessionStatisticsRef = useRef(getSessionStatistics);
  const ensureExerciseLibraryLoadedRef = useRef(ensureExerciseLibraryLoaded);
  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString();
  }, []);
  const thirtyDaysAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString();
  }, []);
  const [last30DaysSessionStatistics, setLast30DaysSessionStatistics] =
    useState<SessionStatistic[]>([]);

  useEffect(() => {
    getSessionStatisticsRef.current = getSessionStatistics;
  }, [getSessionStatistics]);

  useEffect(() => {
    ensureExerciseLibraryLoadedRef.current = ensureExerciseLibraryLoaded;
  }, [ensureExerciseLibraryLoaded]);

  useEffect(() => {
    let isCancelled = false;
    const fetchStatistics = async () => {
      if (!member.id) {
        setLast30DaysSessionStatistics([]);
        return;
      }
      const response = await getSessionStatisticsRef.current(
        [member.id],
        thirtyDaysAgo,
        tomorrow,
        true,
      );
      if (isCancelled) {
        return;
      }
      setLast30DaysSessionStatistics(response?.[member.id] ?? []);
    };
    fetchStatistics();
    return () => {
      isCancelled = true;
    };
  }, [member.id, thirtyDaysAgo, tomorrow]);

  const [selectedWorkout, setSelectedWorkout] =
    useState<SessionStatistic | null>(null);
  const currentMember = members[member.id] ?? member;

  const [exerciseNameOverrides, setExerciseNameOverrides] = useState<
    Record<string, string>
  >({});
  const [isResolvingOneRMNames, setIsResolvingOneRMNames] = useState(false);

  useEffect(() => {
    const recordIds = Object.keys(currentMember.records?.exercises || {});
    const missingIds = recordIds.filter(
      (id) => !mergedExercisesMap[id] && !exerciseNameOverrides[id],
    );

    if (missingIds.length === 0) {
      setIsResolvingOneRMNames(false);
      return;
    }

    let cancelled = false;
    setIsResolvingOneRMNames(true);

    void fetchExercisesByIds(missingIds).then((result) => {
      if (cancelled) return;

      if (result.success && result.data) {
        const names: Record<string, string> = {};
        result.data.forEach((exercise) => {
          if (exercise.id && exercise.name) {
            names[exercise.id] = exercise.name;
          }
        });
        setExerciseNameOverrides((prev) => ({ ...prev, ...names }));
      }

      // Clear regardless of success/failure so we don't block the tab
      // forever if the fetch fails; unresolved ids fall back to raw id.
      setIsResolvingOneRMNames(false);
    });

    return () => {
      cancelled = true;
    };
  // Re-run when the member changes or the global exercise map updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMember.id, mergedExercisesMap]);

  const all1RMs = useMemo(
    () =>
      buildOneRMRows(currentMember, mergedExercisesMap, exerciseNameOverrides),
    [currentMember, mergedExercisesMap, exerciseNameOverrides],
  );
  const [editingExercise, setEditingExercise] = useState<OneRMRow | null>(null);
  const [selectedExercise, setSelectedExercise] = useState<OneRMRow | null>(
    null,
  );
  const [numItemsToShow, setNumItemsToShow] = useState(20);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void ensureExerciseLibraryLoadedRef.current();
  }, []);

  useEffect(() => {
    setNumItemsToShow(20);
  }, [member.id]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (
      !sentinel ||
      numItemsToShow >= all1RMs.length ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNumItemsToShow((prev) => Math.min(all1RMs.length, prev + 20));
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [all1RMs.length, numItemsToShow]);

  const calculateCompliance = (statistic: SessionStatistic) => {
    const totalReps = statistic.totalReps || 0;
    const performedReps = statistic.totalPerformedReps || 0;
    const totalSets = statistic.totalSets || 0;
    const performedSets = statistic.totalPerformedSets || 0;

    const setCompliance = totalSets > 0 ? (performedSets / totalSets) * 100 : 0;
    const repsCompliance =
      totalReps > 0 ? (performedReps / totalReps) * 100 : 0;

    return {
      setCompliance: Math.round(setCompliance),
      repsCompliance: Math.round(repsCompliance),
    };
  };

  const calculateVolume = (statistic: SessionStatistic) => {
    if (!statistic.exercises) return 0;

    let totalVolume = 0;
    Object.values(statistic.exercises).forEach((sets) => {
      sets.forEach((set) => {
        totalVolume += (set.performedWeight || 0) * (set.performedReps || 0);
      });
    });

    return totalVolume;
  };

  const handleSave1RM = async (newValue: number) => {
    if (!editingExercise) return;

    const currentMember = members[member.id] ?? member;
    const newMember = cloneDeep(currentMember);

    if (!newMember.records) {
      newMember.records = { exercises: {} };
    }
    if (!newMember.records.exercises) {
      newMember.records.exercises = {};
    }

    const exerciseId = editingExercise.exerciseId;
    const exerciseRecord = newMember.records.exercises[exerciseId];
    const updatedDate = new Date().toISOString();

    if (exerciseRecord) {
      // Existing record
      if (!exerciseRecord.initial1RM && exerciseRecord.max1RM) {
        const oldMax = exerciseRecord.max1RM;
        if (oldMax.oneRM !== 0) {
          exerciseRecord.initial1RM = {
            oneRM: oldMax.oneRM,
            updatedDate: oldMax.updatedDate,
            unit: 'lb' as const,
          };
        } else {
          exerciseRecord.initial1RM = {
            oneRM: newValue,
            updatedDate,
            unit: 'lb' as const,
          };
        }
      } else if (
        exerciseRecord.initial1RM &&
        exerciseRecord.initial1RM.oneRM === 0
      ) {
        const oldMax = exerciseRecord.max1RM;
        if (oldMax && oldMax.oneRM !== 0) {
          exerciseRecord.initial1RM = {
            oneRM: oldMax.oneRM,
            updatedDate: oldMax.updatedDate,
            unit: 'lb' as const,
          };
        } else {
          exerciseRecord.initial1RM = {
            oneRM: newValue,
            updatedDate,
            unit: 'lb' as const,
          };
        }
      }
      exerciseRecord.max1RM = {
        oneRM: newValue,
        updatedDate,
        unit: 'lb' as const,
        lastUpdateMethod: 'manual',
      };
    } else {
      // New record
      const newRecord = {
        oneRM: newValue,
        updatedDate,
        unit: 'lb' as const,
      };
      newMember.records.exercises[exerciseId] = {
        initial1RM: { ...newRecord },
        max1RM: { ...newRecord, lastUpdateMethod: 'manual' },
      };
    }

    await updateMember(currentMember, newMember);
    const updatedRows = buildOneRMRows(
      newMember,
      mergedExercisesMap,
      exerciseNameOverrides,
    );
    setEditingExercise(null);
    if (selectedExercise && selectedExercise.exerciseId === exerciseId) {
      const updatedRow = updatedRows.find(
        (row) => row.exerciseId === exerciseId,
      );
      if (updatedRow) {
        setSelectedExercise(updatedRow);
      }
    }
  };

  const allStatistics = useMemo(
    () =>
      [...(last30DaysSessionStatistics || [])].sort(
        (a, b) =>
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
      ),
    [last30DaysSessionStatistics],
  );

  const exerciseSetHistory = useMemo(() => {
    const history: Record<
      string,
      { set: ExerciseSetStatistic; date: string }[]
    > = {};
    allStatistics.forEach((session) => {
      if (session.exercises) {
        Object.entries(session.exercises).forEach(([exerciseId, sets]) => {
          if (!history[exerciseId]) {
            history[exerciseId] = [];
          }
          // Reverse the order of sets within each session
          sets
            .slice()
            .reverse()
            .forEach((set) => {
              history[exerciseId].push({ set, date: session.createdDate });
            });
        });
      }
    });
    return history;
  }, [allStatistics]);

  const render1RMRow = useCallback(
    (oneRMRow: OneRMRow) => {
      const {
        exerciseName,
        max1RM,
        lastUpdateDate,
        initial1RM,
        firstUpdateDate,
        growthPercentage,
      } = oneRMRow;

      const hasHistory = exerciseSetHistory[oneRMRow.exerciseId]?.length > 0;

      return (
        <div
          className={classNames(styles.oneRMRow, {
            [styles.clickable]: hasHistory,
          })}
          onClick={() => hasHistory && setSelectedExercise(oneRMRow)}
        >
          <div className={styles.oneRMColumn}>{exerciseName}</div>
          <div className={classNames(styles.oneRMColumn, styles.colRow)}>
            <div className={styles.internalColumn}>
              {max1RM !== '--' ? (
                <>
                  <div>{`${max1RM} lbs`}</div>
                  <div>{formatDate(lastUpdateDate)}</div>
                </>
              ) : (
                <div>--</div>
              )}
            </div>
            <div style={{ width: '50px' }}>
              <EditOutlinedIcon
                className={classNames(styles.editIcon)}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingExercise(oneRMRow);
                }}
              />
            </div>
          </div>
          <div className={styles.oneRMColumn}>
            <div className={styles.internalColumn}>
              {initial1RM !== '--' ? (
                <>
                  <div>{`${initial1RM} lbs`}</div>
                  <div>{formatDate(firstUpdateDate)}</div>
                </>
              ) : (
                <div>--</div>
              )}
            </div>
          </div>
          <div className={styles.oneRMColumn}>
            {growthPercentage !== '--' ? `${growthPercentage}%` : '--'}
          </div>
        </div>
      );
    },
    [exerciseSetHistory],
  );

  const OneRMItemComponent = useCallback(
    ({ data }: { data: OneRMRow }) => render1RMRow(data),
    [render1RMRow],
  );

  const displayed1RMs = useMemo(() => {
    return all1RMs.slice(0, numItemsToShow);
  }, [all1RMs, numItemsToShow]);

  const renderOneRMsList = () => {
    if (isResolvingOneRMNames) {
      return (
        <div className={styles.loaderContainer}>
          <CircularProgress size={24} color="primary" />
        </div>
      );
    }

    return (
      <div>
        <ListContent
          items={displayed1RMs}
          titles={['EXERCISE NAME', 'CURRENT 1RM', 'STARTING 1RM', 'GROWTH %']}
          listHeaderClassName={styles.oneRMGreed}
          ItemComponent={OneRMItemComponent}
          EmptyStateComponent={() => (
            <EmptyState
              icon={<FitnessCenterIcon />}
              text="No 1RM History Available"
            />
          )}
        />
        {numItemsToShow < all1RMs.length && (
          <div ref={sentinelRef} style={{ height: '20px', margin: '10px 0' }} />
        )}
      </div>
    );
  };

  const renderWorkoutRow = (statistic: SessionStatistic) => {
    const { setCompliance, repsCompliance } = calculateCompliance(statistic);
    const volume = calculateVolume(statistic);
    const eccentricCompliance = statistic.goodEccentricReps
      ? (
          (statistic.goodEccentricReps / statistic.aiTotalPerformedReps) *
          100
        ).toFixed(1)
      : 0;

    const concentricCompliance = statistic.goodConcentricReps
      ? (
          (statistic.goodConcentricReps / statistic.aiTotalPerformedReps) *
          100
        ).toFixed(1)
      : 0;

    return (
      <div
        className={styles.workoutRow}
        onClick={() => setSelectedWorkout(statistic)}
      >
        <div className={styles.workoutColumn}>
          {statistic.name || 'Unnamed Workout'}
        </div>
        <div className={styles.workoutColumn}>
          {getFormalDateString(new Date(statistic.createdDate))}
        </div>
        <div className={styles.workoutColumn}>{setCompliance}%</div>
        <div className={styles.workoutColumn}>{repsCompliance}%</div>
        <div className={styles.workoutColumn}>{eccentricCompliance}%</div>
        <div className={styles.workoutColumn}>{concentricCompliance}%</div>
        <div className={styles.workoutColumn}>{volume} lbs</div>
      </div>
    );
  };

  const renderWorkoutsList = () => {
    return (
      <ListContent
        items={allStatistics}
        titles={[
          'WORKOUT NAME',
          'DATE',
          'SET COMPLIANCE',
          'REPS COMPLIANCE',
          'ECCENTRIC COMPLIANCE',
          'CONCENTRIC COMPLIANCE',
          'VOLUME',
        ]}
        listHeaderClassName={styles.workoutsListGrid}
        ItemComponent={({ data }) => renderWorkoutRow(data)}
        EmptyStateComponent={() => (
          <EmptyState
            icon={<FitnessCenterIcon />}
            text="No Workout Statistics Available"
          />
        )}
      />
    );
  };

  const tabSliderTabs: TabSliderTab[] = [
    {
      label: InDepthMemberContentTabs.OneRMs,
      content: renderOneRMsList(),
    },
    {
      label: InDepthMemberContentTabs.Workouts,
      content: renderWorkoutsList(),
    },
  ];

  return (
    <div className={styles.container}>
      <TabSlider tabs={tabSliderTabs} initialTab={0} />
      {selectedWorkout && (
        <WorkoutDetailsDialog
          isOpen={!!selectedWorkout}
          onClose={() => setSelectedWorkout(null)}
          workout={selectedWorkout}
          member={currentMember}
        />
      )}
      {editingExercise && (
        <Edit1RMDialog
          isOpen={!!editingExercise}
          onClose={() => setEditingExercise(null)}
          onSave={handleSave1RM}
          exerciseName={editingExercise.exerciseName}
          currentValue={
            typeof editingExercise.max1RM === 'number'
              ? editingExercise.max1RM
              : 0
          }
          lastUpdateDate={
            editingExercise.lastUpdateDate !== '--'
              ? editingExercise.lastUpdateDate
              : ''
          }
        />
      )}
      {selectedExercise && (
        <ExerciseSetHistoryDialog
          isOpen={!!selectedExercise}
          onClose={() => setSelectedExercise(null)}
          exerciseSets={exerciseSetHistory[selectedExercise.exerciseId] || []}
        />
      )}
    </div>
  );
};

export default InDepthMember;